import React, { useState, useEffect } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useNavigate } from "react-router-dom";

const CookieSettings = () => {
  usePageTitle("Cookie Settings");
  const navigate = useNavigate();
  const [consent, setConsent] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const currentConsent = localStorage.getItem("cookieConsent");
    setConsent(currentConsent);
  }, []);

  const updateConsent = (newConsent) => {
    localStorage.setItem("cookieConsent", newConsent);
    setConsent(newConsent);
    setShowSuccess(true);

    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  const clearConsent = () => {
    localStorage.removeItem("cookieConsent");
    setConsent(null);
    setShowSuccess(true);

    setTimeout(() => {
      setShowSuccess(false);
      navigate("/");
    }, 2000);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-white mb-6">Cookie Settings</h1>

      {showSuccess && (
        <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded mb-6">
          Cookie preferences updated successfully!
        </div>
      )}

      <div className="bg-dark-card border border-gray-700 rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Current Status
        </h2>
        <p className="text-gray-300 mb-4">
          Your current cookie consent status:{" "}
          <span
            className={`font-semibold ${
              consent === "accepted"
                ? "text-green-400"
                : consent === "declined"
                  ? "text-red-400"
                  : "text-yellow-400"
            }`}
          >
            {consent === "accepted"
              ? "Accepted"
              : consent === "declined"
                ? "Declined"
                : "Not Set"}
          </span>
        </p>
      </div>

      <div className="bg-dark-card border border-gray-700 rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Essential Cookies & Storage
        </h2>
        <p className="text-gray-300 mb-3">
          These are necessary for the website to function and cannot be
          disabled:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 text-gray-300">
          <li>
            <strong>Authentication Token:</strong> Keeps you logged in to your
            account (stored in localStorage)
          </li>
          <li>
            <strong>Cookie Consent:</strong> Remembers your cookie preferences
            (stored in localStorage)
          </li>
          <li>
            <strong>Difficulty Mode Preference:</strong> Remembers your last
            selected difficulty mode (Casual/Relaxed) for convenience (stored in
            localStorage)
          </li>
          <li>
            <strong>Activity Tracking:</strong> Records when you last interacted
            with the platform for admin analytics (stored in database, not
            cookies)
          </li>
        </ul>
        <div className="mt-4 flex items-center justify-between bg-dark-bg border border-gray-600 rounded p-4">
          <span className="text-white font-semibold">
            Essential Cookies & Local Storage
          </span>
          <span className="px-3 py-1 bg-green-600/20 text-green-400 text-sm rounded">
            Always Active
          </span>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          <strong>Note:</strong> We use browser localStorage to store essential
          data like authentication tokens and user preferences. This data stays
          on your device and is never shared with third parties.
        </p>
      </div>

      <div className="bg-dark-card border border-gray-700 rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Optional Cookies
        </h2>
        <p className="text-gray-300 mb-3">
          Currently, we do not use any optional cookies for analytics,
          advertising, or tracking. If this changes in the future, you will be
          able to manage them here.
        </p>
        <div className="bg-blue-500/10 border border-blue-500 rounded p-4">
          <p className="text-blue-400 text-sm">
            <strong>Privacy First:</strong> Collect Arena respects your privacy.
            We only store what's necessary to provide you with a seamless quiz
            experience.
          </p>
        </div>
      </div>

      <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Manage Your Preferences
        </h2>
        <div className="space-y-4">
          <button
            onClick={() => updateConsent("accepted")}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            Accept All Cookies
          </button>
          <button
            onClick={() => updateConsent("declined")}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            Decline Optional Cookies
          </button>
          <button
            onClick={clearConsent}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            Reset Preferences (Show Banner Again)
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings;
